import api from "./api";

const SRC = "https://checkout.razorpay.com/v1/checkout.js";
let loading = null;

/** Load Razorpay Checkout on demand (not on every page view). */
export function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (!loading) {
    loading = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      s.onload = () => resolve(true);
      s.onerror = () => { loading = null; resolve(false); };
      document.body.appendChild(s);
    });
  }
  return loading;
}

/**
 * Run the whole online-payment flow for a booking.
 *  1. ask OUR server for an order (the amount is decided server-side)
 *  2. open Razorpay Checkout
 *  3. send the signed result back to OUR server to verify and confirm
 * Resolves { status: "paid" | "dismissed" | "failed", message?, booking_id? } - never rejects.
 */
export async function payForBooking(bookingId, token) {
  const headers = token ? { "X-Booking-Token": token } : {};
  let order;
  try {
    ({ data: order } = await api.post(`/payments/bookings/${bookingId}/order`, {}, { headers }));
  } catch (e) {
    return { status: "failed", message: e.response?.data?.detail || "We couldn't start the payment. Please try again." };
  }
  if (!(await loadRazorpay())) {
    return { status: "failed", message: "The payment window couldn't load. Check your connection and try again." };
  }

  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      order_id: order.order_id,
      amount: order.amount,
      currency: order.currency,
      name: order.name || "ROOM XIII",
      description: `Booking ${order.reference || ""}`.trim(),
      prefill: order.prefill,
      theme: { color: "#C9A24B" },
      modal: { ondismiss: () => resolve({ status: "dismissed" }) },
      handler: async (resp) => {
        try {
          const { data } = await api.post("/payments/razorpay/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          resolve({ status: "paid", booking_id: data.booking_id });
        } catch (e) {
          // Money may have been taken: the webhook will still confirm it. Tell the guest honestly.
          resolve({ status: "failed", verifying: true, message: e.response?.data?.detail || "We're confirming your payment. Please check your booking in a minute." });
        }
      },
    });
    rzp.on("payment.failed", (r) => {
      // Checkout lets the guest retry inside the same modal; only report if they close it.
      // eslint-disable-next-line no-console
      console.debug("payment attempt failed:", r?.error?.description);
    });
    rzp.open();
  });
}
